import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ComparisonDataPoint, Granularity } from '../types';
import { PALETTE } from '../lib/constants';
import { getGranularitySuffix, getGranularityTitle } from '../lib/chart-utils';

/**
 * Area chart N / N-1 — palette verte pour le dashboard souscriptions.
 *
 *  - N : ligne pleine `stroke=#10b981`, gradient 12% → 0%
 *  - N-1 : ligne pointillée `strokeDasharray="4 3"` vert clair `#6ee7b7`
 *  - `connectNulls` : les gaps N-1 (pas de correspondance trouvée) sont
 *    affichés comme des interruptions de ligne (volontaire).
 *
 * Cf SPEC-PORT.md §7.3.A pour la spec visuelle complète.
 */
export interface SouscriptionsAreaProps {
  data: ComparisonDataPoint[];
  granularity: Granularity;
  dateType: 'souscription' | 'effet';
}

export function SouscriptionsArea({ data, granularity, dateType }: SouscriptionsAreaProps) {
  const title = `Evolution ${getGranularityTitle(granularity)}`;
  const subtitle = `${dateType === 'effet' ? `Par date d'effet` : 'Par date de souscription'} · ${getGranularitySuffix(granularity)}`;

  return (
    // `h-full flex flex-col` : la card s'étire à la hauteur de la row de
    // grille (la plus grande des 2 siblings). Le chart container en
    // dessous est `flex-1` pour absorber tout l'espace restant → les 2
    // cards SouscriptionsArea et TopCompagnies ont des tailles alignées.
    <div className="flex h-full flex-col rounded-stripe border border-stripe-border bg-white p-4 shadow-stripe-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-stripe-text">{title}</div>
          <div className="mt-0.5 text-2xs text-stripe-text-muted">{subtitle}</div>
        </div>
        <div className="flex items-center gap-3 text-2xs text-stripe-text-muted">
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-4" style={{ backgroundColor: PALETTE.accent }} />N
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-0.5 w-4"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(to right,#6ee7b7 0 4px,transparent 4px 7px)',
              }}
            />
            N-1
          </span>
        </div>
      </div>

      <div className="mt-4 w-full flex-1" style={{ minHeight: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="souscriptionsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={PALETTE.accent} stopOpacity={0.12} />
                <stop offset="100%" stopColor={PALETTE.accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e3e8ee" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#8898aa' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#8898aa' }}
              width={32}
            />
            <Tooltip
              cursor={{ stroke: '#e3e8ee', strokeWidth: 1 }}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid #e3e8ee',
                boxShadow: '0 2px 5px 0 rgba(0,0,0,.04)',
              }}
            />
            <Legend wrapperStyle={{ display: 'none' }} />
            <Area
              type="monotone"
              dataKey="totalPrev"
              name="N-1"
              stroke={PALETTE.accentN1}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="none"
              connectNulls
            />
            <Area
              type="monotone"
              dataKey="total"
              name="N"
              stroke={PALETTE.accent}
              strokeWidth={2}
              fill="url(#souscriptionsGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
